/**
 * Export Utilities for Admin Dashboard
 * Provides CSV, Excel, and PDF export functionality
 */

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { authFetch } from '../config/api.config';

// CSV Export
export const exportToCSV = (data: any[], filename: string = 'export.csv') => {
  if (!data || data.length === 0) {
    console.warn('No data to export');
    return;
  }

  // Get headers from first object
  const headers = Object.keys(data[0]);

  // Create CSV content
  const csvContent = [
    // Headers
    headers.join(';'),
    // Data rows
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Handle different data types
        if (value === null || value === undefined) return '';
        if (typeof value === 'object') return JSON.stringify(value).replace(/"/g, '""');
        if (typeof value === 'string' && value.includes(';')) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value;
      }).join(';')
    )
  ].join('\n');

  // Add BOM for proper Excel UTF-8 handling
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Excel Export Helper (requires backend support)
export const exportToExcel = async (
  endpoint: string,
  params: any = {},
  filename: string = 'export.xlsx'
) => {
  try {
    // T-009: authFetch setzt die Auth-Header und refresht bei 401 automatisch
    const queryString = new URLSearchParams({ ...params, format: 'excel' }).toString();
    const response = await authFetch(`${endpoint}?${queryString}`, {});

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Excel export failed:', error);
    throw error;
  }
};

// PDF Export Helper (requires backend support)
export const exportToPDF = async (
  endpoint: string,
  params: any = {},
  filename: string = 'export.pdf'
) => {
  try {
    const queryString = new URLSearchParams({ ...params, format: 'pdf' }).toString();
    const response = await authFetch(`${endpoint}?${queryString}`, {});

    if (!response.ok) {
      throw new Error('Export failed');
    }

    const blob = await response.blob();
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('PDF export failed:', error);
    throw error;
  }
};

// Booking Export Formatter
export const formatBookingsForExport = (bookings: any[]) => {
  return bookings.map(booking => ({
    'Buchungsnummer': booking.bookingNumber,
    'Kunde': booking.customerName,
    'E-Mail': booking.customerEmail || '',
    'Telefon': booking.customerPhone || '',
    'Datum': format(new Date(booking.tripDate), 'dd.MM.yyyy', { locale: de }),
    'Tour-Typ': booking.variant || booking.tripType,
    'Teilnehmer': booking.participantCount,
    'Status': translateStatus(booking.status),
    'Zahlungsstatus': translatePaymentStatus(booking.paymentStatus),
    'Gesamtbetrag': formatCurrency(booking.totalPrice || booking.totalAmount),
    'Bezahlt': formatCurrency(booking.depositPaid ? booking.totalPrice : 0),
    'Erstellt am': format(new Date(booking.createdAt), 'dd.MM.yyyy HH:mm', { locale: de }),
    'Notizen': booking.notes || ''
  }));
};

// Calendar Slot Export Formatter
export const formatCalendarSlotsForExport = (slots: any[]) => {
  return slots.map(slot => ({
    'Datum': format(new Date(slot.date), 'dd.MM.yyyy', { locale: de }),
    'Zeit': slot.time,
    'Tour-Typ': slot.tourType,
    'Max. Teilnehmer': slot.maxCapacity,
    'Gebucht': slot.currentBookings,
    'Verfügbar': slot.maxCapacity - slot.currentBookings,
    'Preis pro Person': formatCurrency(slot.pricePerPerson),
    'Status': slot.isAvailable ? 'Verfügbar' : 'Blockiert',
    'Notizen': slot.specialNotes || ''
  }));
};

// Customer Export Formatter
export const formatCustomersForExport = (customers: any[]) => {
  return customers.map(customer => ({
    'Kundennr.': customer.id,
    'Vorname': customer.firstName,
    'Nachname': customer.lastName,
    'E-Mail': customer.email,
    'Telefon': customer.phone || '',
    'Firma': customer.companyName || '',
    'Kundentyp': customer.customerType,
    'Buchungen': customer.totalBookings,
    'Umsatz': formatCurrency(customer.totalRevenue),
    'VIP': customer.isVip ? 'Ja' : 'Nein',
    'Erstellt': format(new Date(customer.createdAt), 'dd.MM.yyyy', { locale: de }),
    'Letzte Buchung': customer.lastBookingDate
      ? format(new Date(customer.lastBookingDate), 'dd.MM.yyyy', { locale: de })
      : '-'
  }));
};

// Payment Export Formatter
export const formatPaymentsForExport = (payments: any[]) => {
  return payments.map(payment => ({
    'Zahlungsnr.': payment.id,
    'Buchungsnr.': payment.bookingNumber,
    'Kunde': payment.customerName,
    'Betrag': formatCurrency(payment.amount),
    'Typ': translatePaymentType(payment.paymentType),
    'Methode': translatePaymentMethod(payment.paymentMethod),
    'Status': translatePaymentStatus(payment.status),
    'Transaktions-ID': payment.transactionId || '',
    'Datum': payment.paidAt
      ? format(new Date(payment.paidAt), 'dd.MM.yyyy HH:mm', { locale: de })
      : '-',
    'Erstellt': format(new Date(payment.createdAt), 'dd.MM.yyyy HH:mm', { locale: de })
  }));
};

// Helper functions
const translateStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'pending': 'Ausstehend',
    'confirmed': 'Bestätigt',
    'cancelled': 'Storniert',
    'completed': 'Abgeschlossen',
    'Draft': 'Entwurf',
    'Confirmed': 'Bestätigt',
    'Cancelled': 'Storniert',
    'Completed': 'Abgeschlossen'
  };
  return statusMap[status] || status;
};

const translatePaymentStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    'pending': 'Ausstehend',
    'partial': 'Teilweise',
    'paid': 'Bezahlt',
    'refunded': 'Erstattet',
    'Pending': 'Ausstehend',
    'Partial': 'Teilweise',
    'Paid': 'Bezahlt',
    'Refunded': 'Erstattet',
    'Failed': 'Fehlgeschlagen'
  };
  return statusMap[status] || status;
};

const translatePaymentType = (type: string): string => {
  const typeMap: { [key: string]: string } = {
    'Deposit': 'Anzahlung',
    'FinalPayment': 'Restzahlung',
    'Refund': 'Erstattung'
  };
  return typeMap[type] || type;
};

const translatePaymentMethod = (method: string): string => {
  const methodMap: { [key: string]: string } = {
    'BankTransfer': 'Überweisung',
    'PayPal': 'PayPal',
    'Stripe': 'Kreditkarte',
    'Cash': 'Bar'
  };
  return methodMap[method] || method;
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount || 0);
};

// Export all utilities
export default {
  exportToCSV,
  exportToExcel,
  exportToPDF,
  formatBookingsForExport,
  formatCalendarSlotsForExport,
  formatCustomersForExport,
  formatPaymentsForExport
};